import _has from "lodash/has";
import { from } from "traverson";
import { configureScope } from "@sentry/react";

import { ALL_DATES, TWELVE_MONTHS, FORMAT, convertIdToReal } from "commons/src/NuDateUtils";
import { clearData } from "commons/src/reducers/indexActions";
import { nucleus } from "commons/src/NucleusTheme";
import { imageBlobToBase64 } from "commons/src/styled/StyledHelper";
import { checkResponseStatus, checkResponseJson } from "commons/src/helpers/ApiResponseHelper";
import { setTheme } from "commons/src/reducers/themeActions";
import { getErrorCode } from "commons/src/helpers/errorHelpers";
import { DONE, FAILED } from "commons/src/helpers/RequestHelpers";
import { ENDPOINTS } from "commons/src/constants/routeConstants";
import { SERVER_ERROR_STATUS, NOT_FOUND_STATUS } from "commons/src/constants/statusCodeConstants";
import {
    successfulLogin,
    setCurrentUser,
    setUserPreferences,
    setDocumentDeliveryPreferences,
    setDocumentDeliveryOptions,
    setResetPassword,
    setRegistrationUser,
    setTermsAccepted,
    setUserLocationId,
    setAdHocFeeAuth
} from "commons/src/reducers/clientPortalUserActions";
import { setDocuments, setDocumentRead } from "commons/src/reducers/documentsActions";
import { convertAccountsToMap } from "commons/src/helpers/accountHelpers";
import { addAnalysis } from "commons/src/reducers/analysisActions";
import {
    setAccounts,
    addJointOwners,
    addAccountDetails,
    addWrapperDetails,
    addPortfolio
} from "commons/src/reducers/accountsActions";
import { getDocumentIdString } from "commons/src/helpers/documentHelpers";
import { handleLinearRetryableRequest } from "commons/src/helpers/RetryHelpers";
import { isInsideGoReactNativeApp } from "commons/src/helpers/ReactNativeAppHelpers";
import { setUnreadDocumentsPresent } from "commons/src/reducers/documentsActions";
import { getDocumentsSinceLastLogin } from "commons/src/helpers/documentHelpers";

import { authFailed, setMsg, authStart, authFinish } from "../reducers/applicationContextActions";
import { setAddresses, setEmailAddresses } from "../reducers/addressesActions";
import { setAdviserDetails, setAdviserLocation } from "../reducers/adviserActions";
import { setBankAccountDetails } from "../reducers/bankAccountsActions";
import { setMemoAssets } from "../reducers/memoAssetsActions";
import {
    redirect as redirectAction,
    clearRedirect as clearRedirectAction
} from "../reducers/navigationActions";
import { newsSuccess, newsError } from "../reducers/newsActions";
import { start, finish, saveLinks } from "../reducers/requestsActions";
import { store } from "../store";
import {
    handleError,
    getAuthenticationMessage,
    getFailedFetchMessage,
    getFailedToFetchPreferencesMessage,
    getFailedToUpdatePreferencesMessage,
    ERROR,
    INFO,
    WARNING
} from "../helpers/ErrorHelpers";
import {
    GET_CLIENT_PREFERENCES,
    CHANGE_PASSWORD,
    CORRESPONDENCE_DELIVERY_PREFERENCE
} from "./DataOpsConstants";
import { routes } from "../helpers/RouteHelpers";

const CRITICAL = true;
const REPORT_ERROR = true;

export function handleFailedAuth({
    error,
    shouldReport = false,
    isForgottenPassword = false,
    failedLogIn = false
}) {
    const errorCode = error.status >= SERVER_ERROR_STATUS ? getErrorCode() : null;
    store.dispatch(authFailed(errorCode, failedLogIn));

    store.dispatch(setMsg(getAuthenticationMessage({ error, isForgottenPassword }), ERROR));

    if (shouldReport) {
        handleError({
            error,
            errorCode,
            isCritical: false,
            severity: ERROR
        });
    }
}

export function handleFailedDataRequest(requestName, error, isCritical, errorMessage) {
    const errorCode = getErrorCode();
    handleError({
        requestName,
        severity: ERROR,
        isCritical,
        error,
        errorCode,
        errorMessage: errorMessage || getFailedFetchMessage(requestName, isCritical, errorCode)
    });
}

export function handleFailedPasswordChange(error) {
    handleError({
        error,
        errorCode: getErrorCode(),
        isCritical: false,
        severity: ERROR,
        requestName: CHANGE_PASSWORD
    });
}

export const retryFetch = async (link, action, requestName, errorMessage) => {
    try {
        store.dispatch(start(requestName));
        const response = await fetch(link, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin"
        }).then(res => res.json());

        store.dispatch(
            action(
                requestName === "correspondences" || requestName === "clientPreferences"
                    ? response
                    : response[requestName]
            )
        );
        store.dispatch(finish(requestName));
    } catch (error) {
        handleFailedDataRequest(requestName, error, !CRITICAL, errorMessage);
    }
};

export function redirect(path, message) {
    store.dispatch(redirectAction(path));

    if (message) {
        store.dispatch(setMsg(message.text, message.severity));
    }
}

export function clearRedirect() {
    store.dispatch(clearRedirectAction());
}

export function followData(trav, follow, postAction, isCritical, prefix = "") {
    if (store.getState().user.isLoggedIn) {
        const requestName = addPotentialPrefix(prefix, follow);
        store.dispatch(start(requestName));
        trav.continue()
            .jsonHal()
            .follow(follow)
            .getResource((error, response, trav) => {
                if (store.getState().user.isLoggedIn) {
                    if (!error) {
                        store.dispatch(finish(requestName));
                        postAction(response, trav);
                    } else {
                        handleFailedDataRequest(requestName, error, isCritical);
                    }
                }
            });
    }
}

export function fromData(fromPoint, postAction, isCritical, requestName, ignore404 = false) {
    if (store.getState().user.isLoggedIn) {
        store.dispatch(start(requestName));

        from(fromPoint)
            .jsonHal()
            .getResource((error, response, trav) => {
                if (store.getState().user.isLoggedIn) {
                    if (!error) {
                        store.dispatch(finish(requestName));
                        postAction(trav, response);
                    } else {
                        if (error.httpStatus === NOT_FOUND_STATUS && ignore404) {
                            store.dispatch(finish(requestName));
                        } else {
                            handleFailedDataRequest(requestName, error, isCritical);
                        }
                    }
                }
            });
    }
}

function addPotentialPrefix(prefix, name) {
    const arr = [name];
    if (prefix) {
        arr.unshift(prefix);
    }
    return arr.join("_");
}

export function initialDataLoad(clientId) {
    fromData(
        `/nucleus-nuclear/nui/nbs/clients/${clientId}`,
        async (trav, response) => {
            store.dispatch(saveLinks(response._links));
            store.dispatch(setCurrentUser(response));

            await getClientPreferences();

            followData(
                trav,
                "adviser",
                (response, adviserTrav) => fetchAdviserLocation(response, adviserTrav),
                CRITICAL
            );
            followData(
                trav,
                "accounts",
                response => fetchAccountSpecifics(response, clientId),
                CRITICAL
            );
            followData(trav, "memoAssets", response =>
                store.dispatch(setMemoAssets(response.memoAssets))
            );
            followData(trav, "bankAccounts", response =>
                store.dispatch(setBankAccountDetails(response.bankAccounts))
            );
            if (!isInsideGoReactNativeApp()) {
                followData(trav, "correspondences", response => {
                    store.dispatch(setDocuments(response));
                    const lastLogin = store.getState().user?.lastLogin;
                    const adviserId = store.getState().user?.adviserId;

                    if (!adviserId) {
                        store.dispatch(
                            setUnreadDocumentsPresent(
                                getDocumentsSinceLastLogin(response, lastLogin).length > 0
                            )
                        );
                    }
                });
            }
            followData(trav, "addresses", response =>
                store.dispatch(setAddresses(response.addresses))
            );
            followData(trav, "emailAddresses", response =>
                store.dispatch(setEmailAddresses(response.emailAddresses))
            );
        },
        CRITICAL,
        clientId
    );
    getNews();
}

export function fetchMoreCorrespondences(link) {
    store.dispatch(start("correspondences"));

    fetch(link, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    })
        .then(res => res.json())
        .then(response => {
            store.dispatch(setDocuments(response));
            store.dispatch(finish("correspondences"));
        })
        .catch(error => {
            handleFailedDataRequest("correspondences", error);
        });
}

export function fetchAdviserLocation(adviserResponse, adviserTrav) {
    store.dispatch(setAdviserDetails(adviserResponse));
    followData(
        adviserTrav,
        "locations",
        (response, locTrav) => {
            store.dispatch(setAdviserLocation(response));
            fetchFirmBranding(locTrav);
        },
        CRITICAL
    );
}

export function fetchFirmBranding(trav) {
    store.dispatch(start("theme"));
    trav.continue()
        .jsonHal()
        .follow("theme")
        .getResource((error, theme) => {
            if (!error) {
                store.dispatch(finish("theme"));
                fetchFirmLogo(theme);
            } else {
                handleError({ error });
                store.dispatch(setTheme(nucleus));
            }
        });
}

function fetchFirmLogo(theme) {
    if (theme._links.logo) {
        fetch(theme._links.logo.href, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin"
        })
            .then(checkResponseStatus("firm logo"))
            .then(res => res.blob())
            .then(blob => imageBlobToBase64(blob))
            .then(base64 => store.dispatch(setTheme({ ...theme, logo: base64 })))
            .catch(error => {
                handleError({ error });
                store.dispatch(setTheme(nucleus));
            });
    } else {
        store.dispatch(setTheme(theme));
    }
}

export function fetchAccountSpecifics(response, clientId) {
    store.dispatch(setAccounts(convertAccountsToMap(response.accounts)));

    response.accounts.forEach(acc => {
        fromData(
            acc._links.accountDetails.href,
            (trav, response) => storeAccountDetailsAndFetchWrapperDetails(trav, response, acc),
            !CRITICAL,
            acc.accountNumber + "_accountDetails"
        );
        fromData(
            acc._links.modelPortfolio.href,
            (trav, response) => fetchHoldingsForFolio(trav, response, acc),
            !CRITICAL,
            acc.accountNumber + "_modelPortfolio",
            true
        );
        if (acc.numberOfOwners > 1) {
            fromData(
                acc._links.accountRelationships.href,
                (trav, response) =>
                    store.dispatch(addJointOwners(acc.accountNumber, response.relationships.OWNR)),
                !CRITICAL,
                acc.accountNumber + "_accountRelationships"
            );
        }
    });

    const allAccountsClosed = response.accounts.every(acc => acc.accountStatus === "Closed");
    fetchPPForClient(clientId, allAccountsClosed ? ALL_DATES.id : TWELVE_MONTHS.id);
}

function storeAccountDetailsAndFetchWrapperDetails(trav, response, acc) {
    store.dispatch(addAccountDetails(acc.accountNumber, response));
    if (_has(store.getState(), `accounts[${acc.accountNumber}].details._links.wrapperDetails`)) {
        followData(trav, "wrapperDetails", response => {
            store.dispatch(addWrapperDetails(acc.accountNumber, response));
        });
    }
}

function fetchHoldingsForFolio(trav, folio, acc) {
    followData(
        trav,
        "holdings",
        holdings => {
            folio.funds = holdings.modelPortfolioHoldings;
            store.dispatch(addPortfolio(acc.accountNumber, folio));
        },
        !CRITICAL,
        acc.accountNumber
    );
}

export function fetchPPForClient(clientId, rangeId) {
    const real = convertIdToReal(rangeId);
    fromData(
        `/nucleus-nuclear/nui/nbs/portfolioperformance/${clientId}?dateFrom=${real.startDate.format(
            FORMAT.UTC_DATE_ONLY
        )}&dateTo=${real.endDate.format(FORMAT.UTC_DATE_ONLY)}`,
        (trav, response) => store.dispatch(addAnalysis(rangeId, response)),
        CRITICAL,
        "portfolioperformance"
    );
}

export async function getClientPreferences() {
    store.dispatch(start(GET_CLIENT_PREFERENCES));
    try {
        const response = await fetch(ENDPOINTS.CLIENT_PREFERENCES, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin"
        })
            .then(checkResponseStatus(ENDPOINTS.CLIENT_PREFERENCES))
            .then(res => res.json());

        store.dispatch(finish(GET_CLIENT_PREFERENCES));
        store.dispatch(setUserPreferences(response));
    } catch (error) {
        handleError({
            error,
            requestName: GET_CLIENT_PREFERENCES,
            errorMessage: getFailedToFetchPreferencesMessage()
        });
    }
}

export async function updateCorrespondenceDeliveryPreference(changedPreference) {
    try {
        store.dispatch(start(CORRESPONDENCE_DELIVERY_PREFERENCE));
        const response = await fetch(ENDPOINTS.CORRESPONDENCE_DELIVERY_PREFERENCE, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin",
            body: JSON.stringify(changedPreference)
        })
            .then(checkResponseStatus(ENDPOINTS.CORRESPONDENCE_DELIVERY_PREFERENCE))
            .then(res => res.json());

        store.dispatch(finish(CORRESPONDENCE_DELIVERY_PREFERENCE));
        store.dispatch(setDocumentDeliveryPreferences(response.currentPreferences));
        return {
            status: DONE,
            message: "Your changes to document delivery preferences have been updated successfully"
        };
    } catch (error) {
        handleFailedDataRequest(CORRESPONDENCE_DELIVERY_PREFERENCE, error, !CRITICAL);
        return {
            status: FAILED,
            message: getFailedToUpdatePreferencesMessage()
        };
    }
}

export async function getCorrespondenceDeliveryOptions() {
    const response = await fetch(ENDPOINTS.CORRESPONDENCE_DELIVERY_OPTIONS, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    })
        .then(checkResponseStatus(ENDPOINTS.CORRESPONDENCE_DELIVERY_PREFERENCE))
        .then(res => res.json());

    store.dispatch(setDocumentDeliveryOptions(response.correspondenceDeliveryOptions));
}

export function authenticate(username, password, resetForm) {
    store.dispatch(authStart());

    return fetch(ENDPOINTS.LOGIN, {
        headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded"
        },
        credentials: "same-origin",
        method: "POST",
        body: `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
    })
        .then(checkResponseStatus(ENDPOINTS.LOGIN))
        .then(r => r.json())
        .then(json => {
            configureScope(scope => {
                scope.setUser({ email: username });
            });
            store.dispatch(successfulLogin(json, username));
        })
        .catch(error => {
            handleFailedAuth({ error, shouldReport: REPORT_ERROR, failedLogIn: true });
            resetForm();
        });
}

export function initialiseUserLocationId() {
    fetch("/nucleus-nuclear/nui/nbs/whoAmI", {
        credentials: "same-origin",
        method: "GET"
    })
        .then(r => r.json())
        .then(json => json.location)
        .then(location => {
            store.dispatch(setUserLocationId(location));
        })
        .catch();
}

export function changePassword(password, newPassword) {
    return fetch(ENDPOINTS.CHANGE_PASSWORD, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify({ password, newPassword })
    })
        .then(checkResponseStatus(ENDPOINTS.CHANGE_PASSWORD))
        .then(r => r.json())
        .then(({ errorCodes }) => {
            if (errorCodes.length === 0) {
                return {
                    status: DONE,
                    message: "You have successfully changed your password."
                };
            } else if (errorCodes.includes("LockedOut")) {
                return logout({
                    message: getAuthenticationMessage({ error: errorCodes }),
                    severity: ERROR
                });
            } else {
                return {
                    status: FAILED,
                    message: getAuthenticationMessage({
                        error: errorCodes,
                        isChangePassword: true
                    })
                };
            }
        })
        .catch(error => {
            handleFailedPasswordChange(error);
            return {
                status: FAILED,
                message: getAuthenticationMessage({
                    error,
                    isChangePassword: true
                })
            };
        });
}

export function registerPost(username, password, newPassword, termsAccepted, successCallback) {
    store.dispatch(authStart());
    fetch(ENDPOINTS.REGISTER, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify({
            username: username.trim(),
            password: password.trim(),
            newPassword: newPassword.trim(),
            termsAccepted,
            app: "CLIENT_PORTAL"
        })
    })
        .then(checkResponseStatus(ENDPOINTS.REGISTER))
        .then(r => r.json())
        .then(json => {
            if (json.errorCodes.length === 0) {
                successCallback(username);
            } else {
                handleFailedAuth({ error: json.errorCodes });
            }
        })
        .catch(error => handleFailedAuth({ error, shouldReport: REPORT_ERROR }));
}

export function forgotPassword(username) {
    store.dispatch(authStart());
    return fetch(ENDPOINTS.RESET_PASSWORD, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify({ username })
    })
        .then(checkResponseStatus(ENDPOINTS.RESET_PASSWORD))
        .then(r => r.json())
        .then(json => {
            if (json.errorCodes.length === 0) {
                store.dispatch(setResetPassword(username));
                store.dispatch(authFinish());
                redirect(routes.public.reset());
            } else {
                handleFailedAuth({ error: json.errorCodes, isForgottenPassword: true });
            }
        })
        .catch(error => handleFailedAuth({ error, shouldReport: REPORT_ERROR }));
}

export function newPassword(username, newPassword, guid) {
    store.dispatch(authStart());
    return fetch(ENDPOINTS.NEW_PASSWORD, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify({
            username: username.trim(),
            guid: guid.trim(),
            newPassword: newPassword.trim()
        })
    })
        .then(checkResponseStatus(ENDPOINTS.NEW_PASSWORD))
        .then(r => r.json())
        .then(json => {
            if (json.errorCodes.length === 0) {
                store.dispatch(authFinish());
                redirect(routes.public.login(), {
                    text: "You have successfully changed your password.",
                    severity: INFO
                });
            } else {
                if (json.errorCodes.includes("RecentlyUsedPass")) {
                    redirect(routes.public.forgotten());
                }
                handleFailedAuth({ error: json.errorCodes });
            }
        })
        .catch(error => handleFailedAuth({ error, shouldReport: REPORT_ERROR }));
}

export function acceptTerms() {
    return fetch(ENDPOINTS.ACCEPT_TERMS, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify({ application: "CLIENT_PORTAL" })
    }).then(res => {
        if (res.ok) {
            store.dispatch(setTermsAccepted());
            redirect(routes.client.root());
        } else {
            throw new Error();
        }
    });
}

export function register(username, regCode, newPassword, termsAccepted) {
    registerPost(username, regCode, newPassword, termsAccepted, username => {
        store.dispatch(setRegistrationUser(username));
        store.dispatch(authFinish());

        redirect(routes.public.login(), {
            text: `Registration successfully completed, you may login in as ${username}`,
            severity: INFO
        });
    });
}

export function markAsRead(documentId, sentDate, currentRetry = 0) {
    fetch(ENDPOINTS.MARK_AS_READ, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "PATCH",
        body: JSON.stringify({ documentId, sentDate })
    })
        .then(checkResponseStatus(ENDPOINTS.MARK_AS_READ))
        .then(r => r.json())
        .then(checkResponseJson(ENDPOINTS.MARK_AS_READ, "firstOpenDate"))
        .then(json => store.dispatch(setDocumentRead(json)))
        .catch(error => {
            handleLinearRetryableRequest({
                currentRetry,
                error,
                retryAction: attemptNumber => markAsRead(documentId, sentDate, attemptNumber),
                onFirstRetry: () =>
                    store.dispatch(
                        setDocumentRead({ documentId, firstOpenDate: new Date().toISOString() })
                    ),
                onMaxRetriesExceeded: () =>
                    handleError({
                        error,
                        requestName: getDocumentIdString(documentId),
                        severity: WARNING
                    }),
                onNonRetryableError: () => handleError({ error })
            });
        });
}

export const logout = ({ message, severity, errorCode = null, isFatal = false, callback }) => {
    store.dispatch(clearData());

    if (message) {
        store.dispatch(setMsg(message, severity, errorCode, isFatal));
    }

    return fetch(ENDPOINTS.LOGOUT, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    })
        .then(() => callback?.())
        .catch(error => handleError({ error }));
};

export const getNews = () => {
    fetch(ENDPOINTS.NEWS_FEED, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    })
        .then(checkResponseStatus(ENDPOINTS.NEWS_FEED))
        .then(r => r.json())
        .then(json => store.dispatch(newsSuccess(json.news)))
        .catch(error => {
            handleError({ error });
            store.dispatch(
                newsError("There has been a problem fetching the news, this will be fixed shortly")
            );
        });
};

export const autoLogInIfAlreadyLoggedIn = () => {
    fetch(ENDPOINTS.KEEP_ALIVE, {
        method: "GET",
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    })
        .then(checkResponseStatus(ENDPOINTS.KEEP_ALIVE))
        .then(r => r.json())
        .then(json => {
            if (json.username) {
                configureScope(scope => {
                    scope.setUser({ email: json.username });
                });
                store.dispatch(successfulLogin(json, json.username));
            }
        })
        .catch(error => handleError({ error }));
};

export const updateAdHocFeeAuth = enabled => dispatch =>
    fetch(ENDPOINTS.AD_HOC_FEE_AUTH, {
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "same-origin",
        method: "PUT",
        body: JSON.stringify({ enabled })
    })
        .then(checkResponseStatus(ENDPOINTS.AD_HOC_FEE_AUTH))
        .then(r => r.json())
        .then(newStatus => dispatch(setAdHocFeeAuth(newStatus)));
